import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Landing from './components/Landing';
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import ManagerDashboard from './pages/Dashboard';
import ClerkDashboard from './pages/DashboardClerk';
import Events from './pages/Events';
import Bookings from './pages/Bookings';
import Reports from './pages/Reports';
import Maintenance from './pages/Maintenance';
import Security from './pages/Security';
import TicketManagement from './pages/TicketManagement';
import Profile from './pages/Profile';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Admin imports
import AdminDashboard from './pages/admin/AdminDashboard';
import EventApproval from './pages/admin/EventApproval';
import FinancialReports from './pages/admin/FinancialReports';
import ShowUpdates from './pages/admin/ShowUpdates';
import ShowDetails from './pages/admin/ShowDetails';
import SecurityManagement from './pages/admin/SecurityManagement';
import EventFlow from './pages/admin/EventFlow';

function PrivateRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <div>Forbidden</div>;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="app-root">
          <div className="app-content">
            <NavBar />
            <ToastContainer position="bottom-right" autoClose={3000} />
            <Routes>
              {/* Common */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/" element={<RootOrLanding />} />

              {/* Existing user routes */}
              <Route path="/dashboard" element={
                <PrivateRoute>
                  <RoleDashboard />
                </PrivateRoute>
              } />
              <Route path="/events" element={
                <PrivateRoute>
                  <Events />
                </PrivateRoute>
              } />
              <Route path="/profile" element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              } />
              <Route path="/bookings" element={
                <PrivateRoute>
                  <Bookings />
                </PrivateRoute>
              } />
              <Route path="/reports" element={
                <PrivateRoute roles={["Admin","Manager","Clerk"]}>
                  <Reports />
                </PrivateRoute>
              } />
              <Route path="/maintenance" element={
                <PrivateRoute roles={["Maintenance","Admin"]}>
                  <Maintenance />
                </PrivateRoute>
              } />
              <Route path="/security" element={
                <PrivateRoute roles={["Security","Admin"]}>
                  <Security />
                </PrivateRoute>
              } />

              {/* Clerk routes */}
              <Route path="/tickets" element={
                <PrivateRoute roles={["Clerk","Admin"]}>
                  <TicketManagement />
                </PrivateRoute>
              } />

              {/* Admin routes */}
              <Route path="/admin" element={
                <PrivateRoute roles={["Admin"]}>
                  <AdminDashboard />
                </PrivateRoute>
              } />
              <Route path="/admin/event-approval" element={
                <PrivateRoute roles={["Admin"]}>
                  <EventApproval />
                </PrivateRoute>
              } />
              <Route path="/admin/financial-reports" element={
                <PrivateRoute roles={["Admin"]}>
                  <FinancialReports />
                </PrivateRoute>
              } />
              <Route path="/admin/show-details" element={
                <PrivateRoute roles={["Admin"]}>
                  <ShowDetails />
                </PrivateRoute>
              } />
              <Route path="/admin/show-updates" element={
                <PrivateRoute roles={["Admin"]}>
                  <ShowUpdates />
                </PrivateRoute>
              } />
              <Route path="/admin/security-management" element={
                <PrivateRoute roles={["Admin"]}>
                  <SecurityManagement />
                </PrivateRoute>
              } />
              <Route path="/admin/event-flow" element={
                <PrivateRoute roles={["Admin"]}>
                  <EventFlow />
                </PrivateRoute>
              } />
            </Routes>
          </div>
          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
}

function RootOrLanding() {
  const { user } = useAuth();
  // If logged in, send to a role-appropriate page; otherwise show public landing page.
  if (user) {
    const role = (user.role || '').toString();
    // Map roles to their landing routes
    const roleRouteMap = {
      'Security': '/security',
      'Admin': '/admin',
      'Clerk': '/dashboard',
      'Manager': '/dashboard',
      'Maintenance': '/maintenance'
    };
    const target = roleRouteMap[role] || '/dashboard';
    return <Navigate to={target} replace />;
  }

  return <Landing />;
}

function RoleDashboard() {
  const { user } = useAuth();
  // Default: if Manager show ManagerDashboard, if Clerk show ClerkDashboard, otherwise fallback to ManagerDashboard
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'Clerk') return <ClerkDashboard />;
  if (user.role === 'Manager') return <ManagerDashboard />;
  // For other roles fall back to ManagerDashboard to give access to overview features
  return <ManagerDashboard />;
}